/**
 * The product rail for sections/nasty-g-classic.liquid, ported from Zap's popular-products Swiper.
 *
 * Zap runs Swiper with `slidesPerView: 1.15 / 2.1 / 3.15`, `spaceBetween: 16 / 20 / 24`,
 * `loop: false`, `speed: 800`, `allowTouchMove: true`, and a ResizeObserver that tears the instance
 * down and rebuilds it across the 576 boundary. Every one of those numbers is geometry, and the
 * geometry lives in the section's stylesheet here, so the rail is a native scroll container: touch
 * drag, momentum, snapping and the wheel are all the platform's, and none of them needs a rebuild
 * on resize.
 *
 * That leaves five jobs the platform does not do, which is all this file is.
 *
 *   1. Advance one card per arrow press. Swiper's `slidesPerGroup` defaults to 1, so a press moves
 *      one card and not one screenful, even at 3.15 per view.
 *   2. Report the ends. Zap's `.swiper-button-disabled` sets `opacity: 0`, so an arrow at the end of
 *      the rail vanishes rather than dimming, and the whole nav is absent when there is nothing to
 *      scroll to at all.
 *   3. Bring a focused card into frame, which the browser does do for free until you add mandatory
 *      snapping and then does not. See reveal().
 *   4. Mouse drag. `allowTouchMove: true` gives Zap a drag on desktop as well as on touch, and a
 *      native scroller only has the touch half.
 *   5. Paint the nav. With this file absent the rail is still a working scroller with every card
 *      reachable by Tab, so an arrow that cannot move anything is never drawn.
 */

const reduced = matchMedia('(prefers-reduced-motion: reduce)');

/*
 * How far the pointer may travel before the gesture is a drag rather than a click. Zap sets
 * `preventClicks: true` for the same reason: the whole card is a link, so a drag that ends over a
 * card must not open it.
 */
const DRAG_SLOP = 8;

/* Sub-pixel slack. A scroller at its end can report a fractional remainder on a fractional layout. */
const EDGE = 2;

class NastyProductRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-rail]');
    this.nav = this.querySelector('[data-rail-nav]');
    this.slides = Array.from(this.querySelectorAll('[data-rail-slide]'));

    if (!this.rail || this.slides.length < 2) return;

    this.steps = Array.from(this.querySelectorAll('[data-rail-step]'));
    for (const step of this.steps) {
      step.addEventListener('click', () => this.step(Number(step.dataset.railStep)));
    }

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.paint();
      });
    };
    this.rail.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new ResizeObserver(() => this.paint());
    this.observer.observe(this.rail);

    this.rail.addEventListener('focusin', (event) => this.reveal(event.target));

    this.bindDrag();

    this.toggleAttribute('data-rail-ready', true);
    this.paint();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.rail?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  /**
   * Distance from the rail's own scroll origin to a slide, which is also that slide's snap
   * position. Measured from rects rather than `offsetLeft`, which is relative to the nearest
   * positioned ancestor and for these slides is the section frame rather than the scroller.
   */
  offsetOf(slide) {
    return slide.getBoundingClientRect().left - this.rail.getBoundingClientRect().left + this.rail.scrollLeft;
  }

  /**
   * Advance one card, which is Swiper's own `slidesPerGroup: 1` even at 3.15 per view.
   *
   * The target is the next slide's snap position rather than the current scroll plus one card
   * width, so the animation's destination and the destination `scroll-snap-type: x mandatory`
   * wants are the same number and there is nothing for the snap to correct on arrival. Past the
   * last slide the target clamps, which is `loop: false`.
   */
  step(direction) {
    const here = this.rail.scrollLeft;
    const offsets = this.slides.map((slide) => this.offsetOf(slide));
    const target =
      direction > 0
        ? offsets.find((offset) => offset > here + EDGE)
        : [...offsets].reverse().find((offset) => offset < here - EDGE);

    this.rail.scrollTo({
      left: target === undefined ? (direction > 0 ? this.rail.scrollWidth : 0) : target,
      behavior: reduced.matches ? 'auto' : 'smooth',
    });
  }

  /**
   * Bring a focused card into frame. The browser normally does this for free, and under
   * `scroll-snap-type: mandatory` it does not: measured at 1440, tabbing to the fourth of six
   * cards left the rail at 0 with 50px of that card in frame, because Chrome resolves its own
   * minimal focus scroll against the snap positions and lands back where it started. Scrolling to
   * the slide's own snap position asks for a number snapping already agrees with.
   *
   * Zap's answer to the same problem is `a11y: { scrollOnFocus: false }`, which is to decide the
   * card does not need to be visible to be focused.
   */
  reveal(target) {
    const slide = target.closest?.('[data-rail-slide]');
    if (!slide) return;

    const port = this.rail.getBoundingClientRect();
    const box = slide.getBoundingClientRect();
    if (box.left >= port.left - EDGE && box.right <= port.right + EDGE) return;

    this.rail.scrollTo({
      left: this.offsetOf(slide),
      behavior: reduced.matches ? 'auto' : 'smooth',
    });
  }

  paint() {
    const slack = this.rail.scrollWidth - this.rail.clientWidth;
    const atStart = this.rail.scrollLeft <= EDGE;
    const atEnd = this.rail.scrollLeft >= slack - EDGE;

    /*
     * Six cards at 3.15 per view always overflow, but a merchant who sets the count to three does
     * not, and neither does a collection with two products in it. Nothing to scroll to, no nav.
     */
    if (this.nav) this.nav.hidden = slack <= EDGE;

    /*
     * The end state goes on the control itself rather than on this host as a `data-` flag the
     * stylesheet reaches down from. Two reasons: `aria-disabled` is the thing a screen reader
     * actually needs, and a rule keyed to the button cannot be beaten by an ancestor's state going
     * stale. Zap's `.swiper-button-disabled` drops the arrow to `opacity: 0`, so it leaves the rail
     * at the end rather than sitting there dimmed, and that is worth keeping.
     */
    for (const step of this.steps) {
      const dead = step.dataset.railStep === '-1' ? atStart : atEnd;
      step.toggleAttribute('data-rail-dead', dead);
      step.setAttribute('aria-disabled', dead ? 'true' : 'false');

      /*
       * A reader who presses Next until the rail stops would otherwise lose focus to the document
       * body and have to Tab from the top of the page again, which is the half of Zap's behaviour
       * that is a bug rather than a look.
       */
      if (dead && step === document.activeElement) {
        this.steps.find((other) => other !== step)?.focus();
      }
    }
  }

  /** Nearest slide snap position to a scroll offset. */
  nearest(offset) {
    const offsets = this.slides.map((slide) => this.offsetOf(slide));
    const limit = this.rail.scrollWidth - this.rail.clientWidth;
    return offsets.reduce(
      (best, next) => (Math.abs(Math.min(next, limit) - offset) < Math.abs(best - offset) ? Math.min(next, limit) : best),
      0
    );
  }

  /**
   * Mouse drag. Touch is left to the platform, which does it better than any script can, so this
   * only claims the pointer types that have no native scroll gesture. `allowTouchMove: true` gives
   * Zap's Swiper both halves; a native scroller only ships the touch one.
   *
   * Snapping is switched off for the duration of the gesture and the rail is settled by hand on
   * release. That is not a preference: `scroll-snap-type: x mandatory` re-snaps after every
   * imperative write to `scrollLeft`, so a drag of 240px measured as a scroll offset of 0, over and
   * over, because 240 is nearer to the snap position it started from than to the next one. With
   * snapping released the offsets stick, and settling explicitly means the rail lands on a card
   * rather than wherever the button happened to come up.
   *
   * Pointer capture is deliberately deferred to the drag threshold rather than taken on
   * pointerdown, because taking it early makes the whole rail unclickable. See the note at the
   * threshold itself.
   */
  bindDrag() {
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let dragging = false;

    this.rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = this.rail.scrollLeft;
    });

    this.rail.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_SLOP) return;
      if (!moved) {
        moved = true;
        this.rail.style.scrollSnapType = 'none';
        this.toggleAttribute('data-dragging', true);

        /*
         * Capture is taken here, at the moment the gesture becomes a drag, and NOT on pointerdown.
         *
         * On pointerdown it broke every card on the rail. Capture retargets the compatibility mouse
         * events as well as the pointer events, so `click` was dispatched to this element instead of
         * to the card link the pointer was actually over, and the anchor never saw a click to act
         * on. Measured on the rendered homepage: a real pointer press on a card left the page on `/`
         * while `element.click()` on the same anchor navigated correctly and reported
         * `defaultPrevented: false`, which is what ruled out the drag blocker below and left capture
         * as the only candidate.
         *
         * Taken at the threshold instead, a click never sets capture at all, so it reaches the link;
         * and a drag has it from its ninth pixel onward, which is what capture was for: keeping the
         * gesture on the rail once the pointer leaves it.
         *
         * It throws NotFoundError when the pointer id is no longer active, so a pointer released
         * between the event and this line must not take the rest of the handler down with it.
         */
        try {
          this.rail.setPointerCapture(event.pointerId);
        } catch {
          /* Without capture the drag still works while the pointer stays over the rail. */
        }
      }
      this.rail.scrollLeft = startLeft - dx;
    });

    const release = () => {
      if (!dragging) return;
      dragging = false;
      this.toggleAttribute('data-dragging', false);
      if (!moved) return;

      const settle = this.nearest(this.rail.scrollLeft);
      this.rail.style.scrollSnapType = '';
      this.rail.scrollTo({ left: settle, behavior: reduced.matches ? 'auto' : 'smooth' });
    };
    this.rail.addEventListener('pointerup', release);
    this.rail.addEventListener('pointercancel', release);

    /*
     * Capture, because the card link would otherwise navigate before this ever ran. `moved` is
     * cleared here and at the next pointerdown rather than on a frame callback: a rail in a
     * backgrounded tab never gets that frame, and the flag then swallows every later click.
     */
    this.rail.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }
}

if (!customElements.get('nasty-product-rail')) {
  customElements.define('nasty-product-rail', NastyProductRail);
}
